import { useMemo } from "react";
import { View, ScrollView } from "react-native";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  DrawerContent,
  DrawerActions,
  Button,
  VStack,
  ActivityIndicator,
  Accordion,
  AccordionPanel,
  useAccordionState,
  useSelectQuery,
} from "@smartrent/ui";
import { FormMultiSelectField } from "@smartrent/forms";
import { PaginatedResponse } from "@smartrent/hooks";
import * as Yup from "yup";

import { instance } from "@/lib/hooks";
import { UserProps } from "@/types";

import { useCMWFeatureFlagDetails } from "../hooks/useCMWFeatureFlagDetails";
import { CMWFeatureFlagState } from "../types";

import { CMWFlagListItem } from "./CMWFlagListItem";
import { CMWFeatureFlagForm } from "./CMWFeatureFlagForm";
import { sharedStyles } from "./styles";

interface UserWithFlags {
  userId: number;
  userName: string;
  flags: Array<CMWFeatureFlagState>;
}

export function CMWUserFlagsDetails({
  organizationId,
  organizationName,
}: {
  organizationId: number;
  organizationName: string;
}) {
  const {
    isAddingFlag,
    hierarchyData,
    hierarchyLoading,
    flagOptions,
    handleAddFlag,
    handleCancelAdd,
    handleToggleFlag,
    handleRemoveFlag,
    handleSaveFlag,
  } = useCMWFeatureFlagDetails("user");

  const accordionProps = useAccordionState({
    allowMultiple: true,
  });

  const userIds = useMemo(() => {
    if (!hierarchyData) {
      return [];
    }

    const orgIdStr = organizationId.toString();
    const orgData = hierarchyData.organizations[orgIdStr];
    const allData = hierarchyData.organizations["all"];

    const orgUserFlags = orgData?.user_level_flags || [];
    const allUserFlags = allData?.user_level_flags || [];

    return Array.from(
      new Set([...orgUserFlags, ...allUserFlags].map((flag) => flag.actor_id))
    );
  }, [hierarchyData, organizationId]);

  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ["users", { ids: userIds.join(",") }],
    queryFn: () =>
      instance
        .get<PaginatedResponse<UserProps>>("/users", {
          params: {
            ids: userIds.length > 0 ? userIds.join(",") : undefined,
          },
        })
        .then((res) => res.data),
    enabled: !hierarchyLoading && userIds.length > 0,
  });

  const userSelectQueryProps = useSelectQuery<PaginatedResponse<UserProps>>(
    (inputValue) => ["users-select", { inputValue, organizationId }] as const,
    async function fetch({ queryKey, pageParam = 1 }) {
      const [, params] = queryKey;
      const inputValue = (
        params as { inputValue?: string; organizationId: number }
      )?.inputValue;
      const orgId = (params as { inputValue?: string; organizationId: number })
        ?.organizationId;

      const orgUsersPromise = instance.get(`/users`, {
        params: {
          email: inputValue ? inputValue : undefined,
          organization_id: orgId,
          page: pageParam,
          limit: 25,
        },
      });

      const platinumUsersPromise = instance.get(`/users`, {
        params: {
          email: inputValue ? inputValue : undefined,
          employee_roles: "Platinum,Gold",
          page: pageParam,
          limit: 25,
        },
      });

      const [orgUsersResponse, platinumUsersResponse] = await Promise.all([
        orgUsersPromise,
        platinumUsersPromise,
      ]);

      const orgUsers = orgUsersResponse.data.records || [];
      const platinumUsers = platinumUsersResponse.data.records || [];

      const allUsers = [...orgUsers, ...platinumUsers];
      const { users: combinedUsers } = allUsers.reduce(
        (
          acc: { userIds: Set<number>; users: UserProps[] },
          user: UserProps
        ) => {
          const userId = Number(user.id);
          if (!acc.userIds.has(userId)) {
            acc.userIds.add(userId);
            acc.users.push(user);
          }
          return acc;
        },
        { userIds: new Set<number>(), users: [] }
      );

      return {
        records: combinedUsers,
        total_pages: Math.max(
          orgUsersResponse.data.total_pages || 1,
          platinumUsersResponse.data.total_pages || 1
        ),
        page: pageParam,
        total_records:
          (orgUsersResponse.data.total_records || 0) +
          (platinumUsersResponse.data.total_records || 0),
      } as PaginatedResponse<UserProps>;
    },
    {},
    {
      inputDebounceInterval: 250,
    }
  );

  const usersWithFlags = useMemo(() => {
    if (!hierarchyData) {
      return [];
    }

    if (userIds.length === 0) {
      return [];
    }

    if (!usersResponse) {
      return [];
    }

    const orgIdStr = organizationId.toString();
    const orgData = hierarchyData.organizations[orgIdStr];
    const allData = hierarchyData.organizations["all"];

    const orgUserFlags = orgData?.user_level_flags || [];
    const allUserFlags = allData?.user_level_flags || [];

    const usersList =
      (usersResponse as PaginatedResponse<UserProps>)?.records || [];

    const userMap = usersList.reduce((map, user: any) => {
      if (user?.id) {
        map.set(Number(user.id), user);
      }
      return map;
    }, new Map<number, UserProps>());

    const userFlagsMap = orgUserFlags.reduce((map, flag) => {
      const userId = flag.actor_id;
      if (!map.has(userId)) {
        map.set(userId, []);
      }
      map.get(userId)!.push({
        enabled: flag.enabled,
        name: flag.name,
      });
      return map;
    }, new Map<number, Array<CMWFeatureFlagState>>());

    allUserFlags.forEach((flag) => {
      const userId = flag.actor_id;
      if (!userFlagsMap.has(userId)) {
        userFlagsMap.set(userId, []);
      }
      const existingFlags = userFlagsMap.get(userId)!;
      const existingFlagNames = new Set(existingFlags.map((f) => f.name));
      if (!existingFlagNames.has(flag.name)) {
        existingFlags.push({
          enabled: flag.enabled,
          name: flag.name,
        });
      }
    });

    const result: UserWithFlags[] = Array.from(userFlagsMap.entries())
      .map(([userId, flags]) => {
        const user = userMap.get(userId);
        if (!user) {
          return null;
        }

        const fullName =
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : null;
        const userName =
          fullName && user.email
            ? `${fullName} - ${user.email}`
            : fullName || user.email || "Unknown User";

        return {
          userId,
          userName,
          flags: flags.sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          ),
        };
      })
      .filter((item): item is UserWithFlags => item !== null)
      .sort((a, b) => a.userName.localeCompare(b.userName));

    return result;
  }, [hierarchyData, usersResponse, organizationId]);

  const handleSaveNewFlag = async (values: {
    featureFlag: string;
    enabled: boolean;
    userIds: number[];
  }) => {
    for (const userId of values.userIds) {
      await handleSaveFlag(
        {
          featureFlag: values.featureFlag,
          enabled: values.enabled,
        },
        userId,
        organizationId
      );
    }
  };

  const isLoading = hierarchyLoading || (userIds.length > 0 && usersLoading);

  if (isLoading) {
    return (
      <View style={sharedStyles.loadingContainer}>
        <ActivityIndicator size={24} />
      </View>
    );
  }

  const formContent = isAddingFlag ? (
    <CMWFeatureFlagForm
      title={`User Flags for ${organizationName}`}
      flagOptions={flagOptions}
      initialValues={{
        userIds: [] as number[],
        featureFlag: "",
        enabled: false,
      }}
      validationSchema={Yup.object({
        userIds: Yup.array()
          .of(Yup.number())
          .min(1, "You must select at least one user")
          .required("You must select at least one user"),
        featureFlag: Yup.string().required("You must select a feature flag"),
      })}
      onSubmit={handleSaveNewFlag}
      onCancel={handleCancelAdd}
      enabledAssistiveText="Whether the feature is enabled or not for this user"
      additionalFields={
        <FormMultiSelectField<UserProps>
          {...userSelectQueryProps}
          name="userIds"
          filtering={true}
          label="Select Users"
          getOptionValue={(option: UserProps) => Number(option.id)}
          getOptionLabel={(option: UserProps) => {
            const userName =
              option.first_name && option.last_name
                ? `${option.first_name} ${option.last_name}`
                : null;
            if (userName && option.email) {
              return `${userName} (${option.email})`;
            }
            if (userName) {
              return userName;
            }
            return option.email;
          }}
          required
        />
      }
    />
  ) : (
    <>
      <DrawerContent>
        <ScrollView>
          <View style={sharedStyles.container}>
            <Typography type="title3">
              User Flags for {organizationName}
            </Typography>
            <VStack spacing={16} style={sharedStyles.content}>
              {usersWithFlags.length === 0 ? (
                <Typography color="textDisabled">
                  No users with flags found
                </Typography>
              ) : (
                <Accordion {...accordionProps} truncate={false}>
                  {usersWithFlags.map((user) => (
                    <AccordionPanel
                      key={user.userId}
                      title={`${user.userName} (${user.flags.length} flag${user.flags.length !== 1 ? "s" : ""})`}
                    >
                      <VStack spacing={16}>
                        {user.flags.length === 0 ? (
                          <Typography color="textDisabled">
                            No user flags
                          </Typography>
                        ) : (
                          <View style={sharedStyles.flagsList}>
                            {user.flags.map((flag, idx) => (
                              <CMWFlagListItem
                                key={flag.name}
                                flagName={flag.name}
                                enabled={flag.enabled}
                                onToggle={() =>
                                  handleToggleFlag(
                                    flag.name,
                                    flag.enabled,
                                    user.userId,
                                    organizationId
                                  )
                                }
                                onRemove={() =>
                                  handleRemoveFlag(flag.name, user.userId)
                                }
                                showSeparator={idx < user.flags.length - 1}
                                switchName={`toggle-${flag.name}-${user.userId}`}
                              />
                            ))}
                          </View>
                        )}
                      </VStack>
                    </AccordionPanel>
                  ))}
                </Accordion>
              )}
            </VStack>
          </View>
        </ScrollView>
      </DrawerContent>
      <DrawerActions>
        <Button onPress={handleAddFlag} style={sharedStyles.fullWidthButton}>
          Add User Flag
        </Button>
      </DrawerActions>
    </>
  );

  return formContent;
}
